import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiAlertTriangle, FiCheckCircle, FiClock, FiCopy, FiHardDrive, FiKey,
  FiPlay, FiRefreshCw, FiSave, FiTerminal, FiZap,
} from 'react-icons/fi';
import { authFetch } from '../auth';

const fmtBytes = (n) => {
  if (n === null || n === undefined) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let x = Number(n); let u = 0;
  while (x >= 1024 && u < units.length - 1) { x /= 1024; u += 1; }
  return `${x.toFixed(1)} ${units[u]}`;
};

const fmtDuration = (ms) => {
  if (ms === null || ms === undefined || !Number.isFinite(Number(ms))) return '—';
  const sec = Math.round(Number(ms) / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ${sec % 60}s`;
  return `${Math.floor(min / 60)}h ${min % 60}m`;
};

// A run that could not start (missing host, path, key, source…) is "skipped":
// it is not a transfer failure, so it should not be labelled as one.
const resultLabel = (run) => {
  if (!run) return '—';
  if (run.ok) return 'success';
  return run.skipped ? 'skipped' : 'failed';
};

function fmtInZone(value, timeZone) {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone, dateStyle: 'medium', timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

const emptyForm = {
  enabled: false,
  host: '',
  port: 22,
  user: '',
  authMode: 'key',
  password: '',
  clearPassword: false,
  keyPath: '',
  remotePath: '',
  sourcePath: '',
  scheduleTime: '02:00',
  timeZone: '',
  mirrorDeletes: false,
  ignoreExisting: false,
  timeoutMinutes: 720,
  bandwidthLimitKbps: 0,
};

export default function AdminBackupView({ API_URL }) {
  const [form, setForm] = useState(emptyForm);
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [apiError, setApiError] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [logLines, setLogLines] = useState(400);
  const [showLog, setShowLog] = useState(false);
  const [streamLive, setStreamLive] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const pollRef = useRef(null);
  const liveRef = useRef(null);
  const wasRunningRef = useRef(false);

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const res = await authFetch(`${API_URL}/admin/backup/status`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const error = new Error(body.message || `Backup API returned HTTP ${res.status}`);
        error.status = res.status;
        throw error;
      }
      const j = await res.json();
      setStatus(j);
      hydrate(j.settings);
      setApiError(null);
    } catch (e) {
      // Kept separate from action errors so a later click cannot hide the fact
      // that this host's server is not answering the backup API at all.
      setApiError({ status: e?.status || null, message: e?.message || 'Failed to load backup status' });
    } finally {
      setLoading(false);
    }
  };

  const hydrate = (s) => {
    if (!s) return;
    setSettings(s);
    setForm((prev) => ({
      ...prev,
      enabled: Boolean(s.enabled),
      host: s.host || '',
      port: s.port || 22,
      user: s.user || '',
      authMode: s.authMode || 'key',
      password: '',
      clearPassword: false,
      keyPath: s.keyPath || '',
      remotePath: s.remotePath || '',
      sourcePath: s.sourcePath || '',
      scheduleTime: s.scheduleTime || '02:00',
      timeZone: s.timeZone || s.defaultTimeZone || '',
      mirrorDeletes: Boolean(s.mirrorDeletes),
      ignoreExisting: Boolean(s.ignoreExisting),
      timeoutMinutes: s.timeoutMinutes || 720,
      bandwidthLimitKbps: s.bandwidthLimitKbps || 0,
    }));
  };

  const refreshStatusOnly = async () => {
    try {
      const res = await authFetch(`${API_URL}/admin/backup/status`);
      if (res.ok) setStatus(await res.json());
    } catch { /* ignore transient refresh errors */ }
  };

  useEffect(() => { load(); }, []);

  const isRunning = Boolean(status?.running);

  // Live updates: subscribe to the server-sent event stream while this view is
  // open. Falls back to polling if the stream is unavailable (for example when
  // a proxy buffers or blocks streaming responses).
  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    const controller = new AbortController();

    const applyUpdate = (payload) => {
      if (!payload || typeof payload !== 'object') return;
      setStatus((prev) => ({ ...(prev || {}), ...payload }));
      if (wasRunningRef.current && payload.running === false) {
        setNotice(payload.lastRun?.ok
          ? 'Backup finished successfully.'
          : `Backup finished: ${payload.lastRun?.message || 'see the log below'}`);
      }
      wasRunningRef.current = Boolean(payload.running);
    };

    const connect = async () => {
      if (cancelled) return;
      try {
        const res = await authFetch(`${API_URL}/admin/backup/stream`, {
          signal: controller.signal,
          headers: { Accept: 'text/event-stream' },
        });
        if (!res.ok || !res.body) throw new Error(`stream unavailable (${res.status})`);
        setStreamLive(true);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (!cancelled) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let split = buffer.indexOf('\n\n');
          while (split >= 0) {
            const frame = buffer.slice(0, split);
            buffer = buffer.slice(split + 2);
            const dataLine = frame.split('\n').find((line) => line.startsWith('data:'));
            if (dataLine) {
              try { applyUpdate(JSON.parse(dataLine.slice(5).trim())); } catch { /* ignore bad frame */ }
            }
            split = buffer.indexOf('\n\n');
          }
        }
      } catch { /* fall through to the polling fallback */ } finally {
        setStreamLive(false);
      }
      if (!cancelled) retryTimer = setTimeout(connect, 5000);
    };

    connect();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      controller.abort();
    };
  }, [API_URL]);

  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    // Only poll while running, and only if the live stream is not connected.
    if (!isRunning || streamLive) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const res = await authFetch(`${API_URL}/admin/backup/status`);
        if (res.ok) {
          const j = await res.json();
          setStatus(j);
          if (!j.running) setNotice(j.lastRun?.ok ? 'Backup finished successfully.' : `Backup finished: ${j.lastRun?.message || 'see log'}`);
        }
      } catch { /* ignore transient refresh errors */ }
    }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [isRunning, streamLive, API_URL]);

  // Ticking clock so the elapsed time stays live between rsync progress lines.
  useEffect(() => {
    if (!isRunning) return undefined;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  // Keep the live transfer panel scrolled to the newest line.
  useEffect(() => {
    if (liveRef.current) liveRef.current.scrollTop = liveRef.current.scrollHeight;
  }, [status?.currentRun?.progressTail]);

  const caps = settings?.capabilities || {};
  const missingBins = useMemo(() => {
    const missing = [];
    if (caps.rsync && !caps.rsync.available) missing.push('rsync');
    if (caps.ssh && !caps.ssh.available) missing.push('ssh');
    if (caps.sshpass && !caps.sshpass.available && form.authMode === 'password') missing.push('sshpass');
    return missing;
  }, [caps, form.authMode]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const buildPayload = () => {
    const payload = {
      enabled: Boolean(form.enabled),
      host: form.host.trim(),
      port: Number(form.port) || 22,
      user: form.user.trim(),
      authMode: form.authMode,
      keyPath: form.keyPath.trim(),
      remotePath: form.remotePath.trim(),
      sourcePath: form.sourcePath.trim(),
      scheduleTime: String(form.scheduleTime || '').trim(),
      timeZone: String(form.timeZone || '').trim(),
      mirrorDeletes: Boolean(form.mirrorDeletes),
      ignoreExisting: Boolean(form.ignoreExisting),
      timeoutMinutes: Number(form.timeoutMinutes) || 720,
      bandwidthLimitKbps: Number(form.bandwidthLimitKbps) || 0,
    };
    if (form.authMode === 'password') {
      payload.password = form.password;
      payload.clearPassword = Boolean(form.clearPassword);
    }
    return payload;
  };

  const save = async () => {
    setSaving(true); setErr(''); setNotice('');
    try {
      const res = await authFetch(`${API_URL}/admin/backup/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: buildPayload() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Failed to save backup settings');
      setSettings(body.settings);
      hydrate(body.settings);
      setNotice(body.nextRunAt
        ? `Saved. Next backup: ${fmtInZone(body.nextRunAt, body.settings?.timeZone)}.`
        : 'Saved. Enable the nightly backup to schedule runs.');
      await refreshStatusOnly();
    } catch (e) {
      setErr(e?.message || 'Failed to save backup settings');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true); setErr(''); setNotice(''); setTestResult(null);
    try {
      // Persist first so the test uses exactly what is on screen.
      const saveRes = await authFetch(`${API_URL}/admin/backup/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: buildPayload() }),
      });
      const saveBody = await saveRes.json().catch(() => ({}));
      if (!saveRes.ok) throw new Error(saveBody.message || 'Failed to save settings before testing');
      setSettings(saveBody.settings);
      const res = await authFetch(`${API_URL}/admin/backup/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Connection test failed');
      setTestResult(body);
    } catch (e) {
      setErr(e?.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const runNow = async () => {
    setStarting(true); setErr(''); setNotice('');
    try {
      const saveRes = await authFetch(`${API_URL}/admin/backup/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: buildPayload() }),
      });
      const saveBody = await saveRes.json().catch(() => ({}));
      if (!saveRes.ok) throw new Error(saveBody.message || 'Failed to save settings before running');
      setSettings(saveBody.settings);
      const res = await authFetch(`${API_URL}/admin/backup/run`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Failed to start backup');
      setStatus(body.status);
      setNotice('Backup started. Status refreshes automatically.');
      setShowLog(true);
    } catch (e) {
      setErr(e?.message || 'Failed to start backup');
    } finally {
      setStarting(false);
    }
  };

  const generateKey = async () => {
    setErr(''); setNotice('');
    try {
      const res = await authFetch(`${API_URL}/admin/backup/key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ regenerate: false }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Failed to create SSH key');
      setSettings(body.settings);
      hydrate(body.settings);
      setNotice('SSH key ready. Add the public key below to the destination server.');
    } catch (e) {
      setErr(e?.message || 'Failed to create SSH key');
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(settings?.publicKey || '');
      setNotice('Public key copied to clipboard.');
    } catch {
      setErr('Could not copy to clipboard; select the text manually.');
    }
  };

  const loadLog = async () => {
    try {
      const res = await authFetch(`${API_URL}/admin/backup/log?lines=${logLines}`);
      if (!res.ok) throw new Error('Failed to load log');
      const body = await res.json();
      setStatus((prev) => ({ ...(prev || {}), logTail: body.log }));
      setShowLog(true);
    } catch (e) {
      setErr(e?.message || 'Failed to load log');
    }
  };

  const lastRun = status?.lastRun;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><FiHardDrive /> Nightly Backup</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={load} disabled={loading} title="Reload settings and status">
            <FiRefreshCw style={{ verticalAlign: 'middle' }} /> {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button className="btn" onClick={testConnection} disabled={testing || isRunning || Boolean(apiError)} title="Check the SSH destination and remote path">
            <FiZap style={{ verticalAlign: 'middle' }} /> {testing ? 'Testing…' : 'Test Connection'}
          </button>
          <button className="btn btn-primary" onClick={runNow} disabled={starting || isRunning || Boolean(apiError)} title="Start a backup immediately">
            <FiPlay style={{ verticalAlign: 'middle' }} /> {isRunning ? 'Backup running…' : 'Run Backup Now'}
          </button>
        </div>
      </div>

      {err && (
        <div style={{ marginTop: 10, padding: 10, border: '1px solid #e57373', background: '#ffebee', color: '#b71c1c', borderRadius: 6 }}>
          <FiAlertTriangle /> {err}
        </div>
      )}
      {apiError && (
        <div style={{ marginTop: 10, padding: 10, border: '1px solid #e57373', background: '#ffebee', color: '#b71c1c', borderRadius: 6 }}>
          <div style={{ fontWeight: 600 }}>
            <FiAlertTriangle /> Backup API unavailable on this server
            {apiError.status ? ` (HTTP ${apiError.status})` : ''}: {apiError.message}
          </div>
          <div style={{ marginTop: 6, fontWeight: 400 }}>
            Requests go to <code>{API_URL}/admin/backup/status</code>. A 404 means this host is
            running an older server build: deploy the package (it now includes
            <code> server/backup.js</code>) and run <code>pm2 restart sparta-server</code>. The
            startup log should show <code>Backup service ready</code>.
          </div>
        </div>
      )}
      {notice && !err && (
        <div style={{ marginTop: 10, padding: 10, border: '1px solid #81c784', background: '#e8f5e9', color: '#1b5e20', borderRadius: 6 }}>
          <FiCheckCircle /> {notice}
        </div>
      )}
      {missingBins.length > 0 && (
        <div style={{ marginTop: 10, padding: 10, border: '1px solid #ffb74d', background: '#fff8e1', color: '#8d6e00', borderRadius: 6 }}>
          <FiAlertTriangle /> Missing on this server: {missingBins.join(', ')}. Install with
          {' '}<code>sudo apt -y install rsync openssh-client{missingBins.includes('sshpass') ? ' sshpass' : ''}</code>.
        </div>
      )}

      <div style={{ ...card, marginTop: 12 }}>
        <h3 style={h3}>Schedule</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />
            Run a nightly backup
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Time
            <input type="time" value={form.scheduleTime} onChange={(e) => set('scheduleTime', e.target.value)} style={{ ...input, width: 120 }} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Time zone
            <input
              value={form.timeZone}
              onChange={(e) => set('timeZone', e.target.value)}
              placeholder={settings?.defaultTimeZone || 'America/New_York'}
              style={{ ...input, width: 220 }}
            />
          </label>
        </div>
        <div style={{ marginTop: 8, color: 'var(--text-soft)' }}>
          <FiClock style={{ verticalAlign: 'middle' }} />{' '}
          {form.enabled
            ? `Next run: ${status?.nextRunAt ? fmtInZone(status.nextRunAt, settings?.timeZone) : 'pending save'}`
            : 'Scheduling is off.'}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
        <div style={card}>
          <h3 style={h3}>Destination (SSH)</h3>
          <Field label="Host">
            <input value={form.host} onChange={(e) => set('host', e.target.value)} placeholder="backup.example.com" style={{ ...input, width: 260 }} />
          </Field>
          <Field label="SSH port">
            <input type="number" min={1} max={65535} value={form.port} onChange={(e) => set('port', e.target.value)} style={{ ...input, width: 90 }} />
          </Field>
          <Field label="SSH user">
            <input value={form.user} onChange={(e) => set('user', e.target.value)} placeholder="sparta" style={{ ...input, width: 200 }} />
          </Field>
          <Field label="Remote path">
            <input value={form.remotePath} onChange={(e) => set('remotePath', e.target.value)} placeholder="/backups/sparta" style={{ ...input, width: 300 }} />
          </Field>
          {form.host && form.user && form.remotePath && (
            <div style={{ color: 'var(--text-soft)', fontSize: 12 }}>
              Target: <code>{form.user}@{form.host}:{form.remotePath}</code>
            </div>
          )}
        </div>

        <div style={card}>
          <h3 style={h3}><FiKey style={{ verticalAlign: 'middle' }} /> Authentication</h3>
          <Field label="Method">
            <select value={form.authMode} onChange={(e) => set('authMode', e.target.value)} style={{ ...input, width: 200 }}>
              <option value="key">SSH key (recommended)</option>
              <option value="password">Password (sshpass)</option>
            </select>
          </Field>

          {form.authMode === 'key' ? (
            <div>
              <Field label="Private key path">
                <input
                  value={form.keyPath}
                  onChange={(e) => set('keyPath', e.target.value)}
                  placeholder={settings?.resolvedKeyPath || 'app-managed key'}
                  style={{ ...input, width: 300 }}
                />
              </Field>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                <button className="btn" onClick={generateKey} disabled={Boolean(apiError)} title="Create an ed25519 key pair on the server">Generate key pair</button>
                {settings?.keyExists && <span style={{ color: 'var(--text-soft)', fontSize: 12 }}>Key present{settings.publicKey ? '' : ' (public key not found)'}</span>}
              </div>
              {settings?.publicKey && (
                <div>
                  <div style={{ color: 'var(--text-soft)', fontSize: 12, marginBottom: 4 }}>
                    Add this line to <code>~/.ssh/authorized_keys</code> on the destination for user <code>{form.user || 'the SSH user'}</code>:
                  </div>
                  <textarea readOnly value={settings.publicKey} rows={3} style={{ ...input, width: '100%', fontFamily: 'monospace', fontSize: 12 }} />
                  <button className="btn" onClick={copyKey}><FiCopy style={{ verticalAlign: 'middle' }} /> Copy public key</button>
                </div>
              )}
            </div>
          ) : (
            <div>
              <Field label="Password">
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => set('password', e.target.value)}
                  placeholder={settings?.hasPassword ? '(stored — leave blank to keep)' : 'destination password'}
                  disabled={form.clearPassword}
                  style={{ ...input, width: 240 }}
                />
              </Field>
              {settings?.hasPassword && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <input type="checkbox" checked={form.clearPassword} onChange={(e) => set('clearPassword', e.target.checked)} />
                  Clear the stored password
                </label>
              )}
              <div style={{ color: 'var(--text-soft)', fontSize: 12 }}>
                Stored on the server in <code>server/data/backup-config.json</code> (0600) and never returned to the browser.
                Requires the <code>sshpass</code> package. Prefer the key option where possible.
              </div>
            </div>
          )}
        </div>

        <div style={card}>
          <h3 style={h3}>What gets copied</h3>
          <Field label="Source path">
            <input
              value={form.sourcePath}
              onChange={(e) => set('sourcePath', e.target.value)}
              placeholder={settings?.defaultSourcePath || '(not configured)'}
              style={{ ...input, width: '100%' }}
            />
          </Field>
          <div style={{ color: 'var(--text-soft)', fontSize: 12, marginBottom: 8 }}>
            Effective source: <code>{settings?.resolvedSourcePath || '—'}</code>
            <br />
            Leave blank to back up the app storage root, which contains every cabinet&apos;s uploads, text, audit and trash folders.
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={form.mirrorDeletes} onChange={(e) => set('mirrorDeletes', e.target.checked)} />
            Mirror deletions (remove destination files that no longer exist here)
          </label>
          <div style={{ color: 'var(--text-soft)', fontSize: 12, marginTop: 4 }}>
            Leave this off for an additive backup that never deletes anything on the destination.
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <input type="checkbox" checked={form.ignoreExisting} onChange={(e) => set('ignoreExisting', e.target.checked)} />
            Only add new files — never update files that already exist there
          </label>
          <div style={{ color: 'var(--text-soft)', fontSize: 12, marginTop: 4 }}>
            Off (recommended): unchanged files are skipped via rsync&apos;s size + modified-time
            check, while new <em>and changed</em> files are copied. On, the destination is
            append-only, so a corrected or re-uploaded document never replaces its older copy.
          </div>
          <div style={{ color: 'var(--text-soft)', fontSize: 12, marginTop: 10 }}>
            <strong>Times:</strong> archive mode (<code>-a</code>) preserves <em>modified</em> times,
            which is what makes later runs skip unchanged files. Creation (birth) times cannot be
            preserved here: Ubuntu&apos;s rsync reports <code>no crtimes</code> because Linux has no
            API to set birth time, and requesting it makes rsync abort with a usage error.
          </div>
        </div>

        <div style={card}>
          <h3 style={h3}>Transfer options</h3>
          <div style={{ color: 'var(--text-soft)', fontSize: 12, marginBottom: 8 }}>
            Transfer flags: <code>-a --partial --stats --timeout=180</code>
            {' '}(<code>-a</code> already includes <code>-r</code> and <code>-t</code>)
            {settings?.features?.mkpath ? ' + --mkpath' : ''}
            {settings?.features?.protectArgs ? ' + -s' : ''}
            {settings?.features?.progress2 ? ' + --info=name,progress2' : ''}
            {form.mirrorDeletes ? ' + --delete' : ''}
            {form.ignoreExisting ? ' + --ignore-existing' : ''}
          </div>
          <Field label="Timeout (minutes)">
            <input type="number" min={1} max={10080} value={form.timeoutMinutes} onChange={(e) => set('timeoutMinutes', e.target.value)} style={{ ...input, width: 110 }} />
          </Field>
          <Field label="Bandwidth limit (KB/s)">
            <input type="number" min={0} value={form.bandwidthLimitKbps} onChange={(e) => set('bandwidthLimitKbps', e.target.value)} style={{ ...input, width: 120 }} />
          </Field>
          <div style={{ color: 'var(--text-soft)', fontSize: 12 }}>0 means unlimited.</div>
          <div style={{ marginTop: 10 }}>
            <button className="btn btn-primary" onClick={save} disabled={saving || Boolean(apiError)}>
              <FiSave style={{ verticalAlign: 'middle' }} /> {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>

      {testResult && (
        <div style={{ ...card, marginTop: 12 }}>
          <h3 style={h3}>Connection test {testResult.ok ? '— passed' : '— failed'}</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {(testResult.steps || []).map((step, idx) => (
                <tr key={`${step.name}-${idx}`}>
                  <td style={{ ...td, width: 40 }}>{step.ok ? '✅' : '❌'}</td>
                  <td style={td}>{step.name}</td>
                  <td style={{ ...td, color: 'var(--text-soft)', fontFamily: 'monospace', fontSize: 12 }}>{step.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ ...card, marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <h3 style={{ ...h3, marginBottom: 0 }}>Status</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: streamLive ? '#2e7d32' : 'var(--text-soft)' }}>
              {streamLive ? '● live updates' : '○ polling'}
            </span>
            <label style={{ color: 'var(--text-soft)', fontSize: 12 }}>
              Log lines
              <input type="number" min={20} max={2000} value={logLines} onChange={(e) => setLogLines(e.target.value)} style={{ ...input, width: 80, marginLeft: 6 }} />
            </label>
            <button className="btn" onClick={loadLog}><FiTerminal style={{ verticalAlign: 'middle' }} /> {showLog ? 'Reload log' : 'Show log'}</button>
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          <Row k="Nightly backup" v={form.enabled ? 'enabled' : 'disabled'} />
          <Row
            k="Currently running"
            v={isRunning
              ? `yes — ${status?.currentRun?.trigger || 'run'}, ${fmtDuration(Math.max(0, nowTick - new Date(status?.currentRun?.startedAt).getTime()))} elapsed (started ${fmtInZone(status?.currentRun?.startedAt, settings?.timeZone)})`
              : 'no'}
          />
          <Row k="Next run" v={status?.nextRunAt ? `${fmtInZone(status.nextRunAt, settings?.timeZone)} (${settings?.timeZone})` : '—'} />
          {isRunning ? (
            <div style={{
              marginTop: 8, padding: 8, borderRadius: 6,
              background: 'var(--hover-bg)', color: 'var(--text-soft)', fontSize: 12,
            }}>
              Previous completed run: {lastRun ? `${fmtInZone(lastRun.startedAt, settings?.timeZone)} — ${resultLabel(lastRun)}` : 'none'}
              {lastRun && !lastRun.ok && lastRun.message ? ` (${lastRun.message})` : ''}
            </div>
          ) : (
            <>
              <Row k="Last run" v={lastRun ? `${fmtInZone(lastRun.startedAt, settings?.timeZone)} — ${lastRun.trigger}` : 'never'} />
              <Row k="Result" v={lastRun ? resultLabel(lastRun) : '—'} />
              <Row k="Duration" v={lastRun ? fmtDuration(lastRun.durationMs) : '—'} />
              <Row k="Files transferred" v={lastRun?.summary?.transferredFiles ?? '—'} />
              <Row k="Data sent" v={lastRun?.summary?.bytesSent != null ? fmtBytes(lastRun.summary.bytesSent) : '—'} />
              <Row k="Exit code" v={lastRun?.exitCode ?? '—'} />
              {lastRun?.message && (
                <div style={{ marginTop: 6, color: lastRun.ok ? 'var(--text)' : (lastRun.skipped ? '#8d6e00' : '#b71c1c') }}>{lastRun.message}</div>
              )}
              {Array.isArray(lastRun?.summary?.errors) && lastRun.summary.errors.length > 0 && (
                <div style={{ marginTop: 6, color: '#b71c1c', fontFamily: 'monospace', fontSize: 12 }}>{lastRun.summary.errors.join('\n')}</div>
              )}
            </>
          )}
        </div>
        {isRunning && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              Live transfer output <span style={{ color: 'var(--text-soft)', fontWeight: 400, fontSize: 12 }}>(newest at the bottom)</span>
            </div>
            <pre ref={liveRef} style={{
              margin: 0, maxHeight: 260, overflow: 'auto', background: '#0b1a0b', color: '#c8facc',
              padding: 10, borderRadius: 6, fontSize: 12, lineHeight: 1.4,
            }}>{status?.currentRun?.progressTail || 'Transfer starting… (rsync lists each file as it moves)'}</pre>
          </div>
        )}
        {showLog && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              Log {isRunning ? '— previous completed run' : ''}
            </div>
            <pre style={{
              margin: 0, maxHeight: 320, overflow: 'auto', background: '#111', color: '#ddd',
              padding: 10, borderRadius: 6, fontSize: 12, lineHeight: 1.4,
            }}>{status?.logTail || 'No backup log yet.'}</pre>
          </div>
        )}
        {Array.isArray(status?.runs) && status.runs.length > 1 && (
          <div style={{ marginTop: 10 }}>
            <h4 style={{ margin: '0 0 6px' }}>Recent runs</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Started</th>
                  <th style={th}>Trigger</th>
                  <th style={th}>Result</th>
                  <th style={th}>Files</th>
                  <th style={th}>Duration</th>
                </tr>
              </thead>
              <tbody>
                {status.runs.map((run) => (
                  <tr key={run.id}>
                    <td style={td}>{fmtInZone(run.startedAt, settings?.timeZone)}</td>
                    <td style={td}>{run.trigger}</td>
                    <td style={{ ...td, color: run.ok ? 'var(--text)' : (run.skipped ? '#8d6e00' : '#b71c1c') }}>{resultLabel(run)}</td>
                    <td style={td}>{run.summary?.transferredFiles ?? '—'}</td>
                    <td style={td}>{fmtDuration(run.durationMs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <div style={{ width: 150, color: 'var(--text-soft)' }}>{label}</div>
      {children}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
      <div style={{ color: 'var(--text-soft)' }}>{k}</div>
      <div style={{ textAlign: 'right' }}>{v}</div>
    </div>
  );
}

const card = { padding: 12, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8 };
const h3 = { margin: 0, marginBottom: 8, fontSize: 16 };
const th = { textAlign: 'left', padding: '6px 8px', background: 'var(--table-head-bg)', borderBottom: '1px solid var(--border)' };
const td = { padding: '6px 8px', borderBottom: '1px solid var(--border)' };
const input = { padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 6 };
